/**
 * childWorkflowRegistry.js — demo registry of child workflow payloads keyed
 * by refName (and optionally refVersion). Canvas V2's SubWorkflow inline
 * drill-in (#168) consults this on-click: given a SubWorkflow node with
 * `config.subWorkflowName`, we look up the child's `{nodes, connections}`
 * payload here, stamp it onto the parent's `config._embeddedCanvas`, and
 * re-layout — the hierarchical ELK pipeline nests the child DAG inside the
 * parent's SubWorkflow container chrome.
 *
 * Why a demo registry
 * ───────────────────
 * The backend route that returns a workflow's canvas payload by refName is
 * wired (`GET /api/v1/workflows/{refName}` → canvas JSON) but the live
 * fetch path introduces loading states + error surfaces that would
 * complicate the first cut. This registry lets us prove the drill-in
 * layout / re-flow / breadcrumb flow end-to-end without waiting on the
 * network, and then Phase 2 (#168b) swaps `lookupChildCanvas` for an
 * async API call while keeping the rest of the wiring identical.
 *
 * Each child is hand-crafted so clicking the corresponding SubWorkflow on
 * the stress-test fixture expands into a meaningful, visually distinct
 * mini-DAG — the user immediately sees a feature Conductor OSS can't match
 * (Conductor OSS forces a page navigation to see any sub-workflow's body).
 */

// ── Health claim evaluation — matches n_health_sub_ref_a23152 ──────────
// A moderately-sized child: two sequential HTTP calls, then a 2-arm
// switch on coverage tier, each arm doing a distinct calculation.
const HEALTH_CLAIM_EVALUATION = {
  workflowMeta: {
    name: 'health_claim_evaluation',
    description: 'Health claim evaluation subflow — eligibility, coverage lookup, tier-based calc.',
  },
  nodes: [
    { id: 'hce_start',         type: 'manual_start',   label: 'Start (hce)', config: {} },
    { id: 'hce_eligibility',   type: 'http_task',      label: 'Check eligibility', config: { method: 'GET', url: '/eligibility' } },
    { id: 'hce_coverage',      type: 'http_task',      label: 'Load coverage rules', config: { method: 'GET', url: '/coverage' } },
    { id: 'hce_tier_switch',   type: 'condition',      label: 'Tier Switch', config: {
      expression: 'coverageTier', sourceType: 'SWITCH', evaluatorType: 'value-param',
    } },
    { id: 'hce_tier_gold',     type: 'script_task',    label: 'Gold-tier calc',   config: {} },
    { id: 'hce_tier_standard', type: 'script_task',    label: 'Standard calc',    config: {} },
    { id: 'hce_conv_tier',     type: 'convergence',    label: '// Marks end of switch hce_tier', config: {
      switchRef: 'hce_tier', isConvergence: true,
    } },
    { id: 'hce_finalize',      type: 'data',           label: 'Finalize decision', config: {} },
    { id: 'hce_end',           type: 'end',            label: 'Done (hce)',       config: {} },
  ],
  connections: [
    { id: 'hce_c1', source: 'hce_start',       target: 'hce_eligibility', sourceHandle: 'default' },
    { id: 'hce_c2', source: 'hce_eligibility', target: 'hce_coverage',    sourceHandle: 'default' },
    { id: 'hce_c3', source: 'hce_coverage',    target: 'hce_tier_switch', sourceHandle: 'default' },
    { id: 'hce_c4', source: 'hce_tier_switch', target: 'hce_tier_gold',   sourceHandle: 'gold' },
    { id: 'hce_c5', source: 'hce_tier_switch', target: 'hce_tier_standard', sourceHandle: 'default' },
    { id: 'hce_c6', source: 'hce_tier_gold',   target: 'hce_conv_tier',   sourceHandle: 'default' },
    { id: 'hce_c7', source: 'hce_tier_standard', target: 'hce_conv_tier', sourceHandle: 'default' },
    { id: 'hce_c8', source: 'hce_conv_tier',   target: 'hce_finalize',    sourceHandle: 'default' },
    { id: 'hce_c9', source: 'hce_finalize',    target: 'hce_end',         sourceHandle: 'default' },
  ],
};

// ── Payout processor — matches n_start_payout_ref_efae41 (async) ──────
// A simple linear flow: compute amount, send transfer, notify.
const PAYOUT_PROCESSOR = {
  workflowMeta: {
    name: 'payout_processor',
    description: 'Issues an approved claim payout via bank transfer and sends notification.',
  },
  nodes: [
    { id: 'pp_start',      type: 'manual_start', label: 'Start (payout)', config: {} },
    { id: 'pp_compute',    type: 'script_task',  label: 'Compute payout amount', config: {} },
    { id: 'pp_transfer',   type: 'http_task',    label: 'Send bank transfer', config: { method: 'POST', url: '/transfers' } },
    { id: 'pp_notify',     type: 'notification_task', label: 'Notify claimant', config: {} },
    { id: 'pp_end',        type: 'end',          label: 'Paid', config: {} },
  ],
  connections: [
    { id: 'pp_c1', source: 'pp_start',    target: 'pp_compute',  sourceHandle: 'default' },
    { id: 'pp_c2', source: 'pp_compute',  target: 'pp_transfer', sourceHandle: 'default' },
    { id: 'pp_c3', source: 'pp_transfer', target: 'pp_notify',   sourceHandle: 'default' },
    { id: 'pp_c4', source: 'pp_notify',   target: 'pp_end',      sourceHandle: 'default' },
  ],
};

// ── KYC check — matches the SUBWF_FIXTURE demo (kyc_check) ─────────────
// Kept rich so the SubWorkflow smoke fixture also benefits from the new
// drill-in path (replaces the flat _embeddedDagPreview with a real child).
const KYC_CHECK = {
  workflowMeta: {
    name: 'kyc_check',
    description: 'Know-your-customer verification with parallel checks.',
  },
  nodes: [
    { id: 'kyc_start',   type: 'manual_start',  label: 'Start (kyc)', config: {} },
    { id: 'kyc_fork',    type: 'fork',          label: 'Parallel Checks', config: { sourceType: 'FORK_JOIN' } },
    { id: 'kyc_id',      type: 'http_task',     label: 'Verify ID doc', config: {} },
    { id: 'kyc_sanctions', type: 'http_task',   label: 'Sanctions screen', config: {} },
    { id: 'kyc_join',    type: 'join',          label: 'Join checks', config: { sourceType: 'JOIN' } },
    { id: 'kyc_score',   type: 'script_task',   label: 'Compute KYC score', config: {} },
    { id: 'kyc_end',     type: 'end',           label: 'KYC done', config: {} },
  ],
  connections: [
    { id: 'kyc_c1', source: 'kyc_start', target: 'kyc_fork', sourceHandle: 'default' },
    { id: 'kyc_c2', source: 'kyc_fork',  target: 'kyc_id',        sourceHandle: 'branch_0' },
    { id: 'kyc_c3', source: 'kyc_fork',  target: 'kyc_sanctions', sourceHandle: 'branch_1' },
    { id: 'kyc_c4', source: 'kyc_fork',  target: 'kyc_join', sourceHandle: 'default' },
    { id: 'kyc_c5', source: 'kyc_id',        target: 'kyc_join', sourceHandle: 'default' },
    { id: 'kyc_c6', source: 'kyc_sanctions', target: 'kyc_join', sourceHandle: 'default' },
    { id: 'kyc_c7', source: 'kyc_join',  target: 'kyc_score', sourceHandle: 'default' },
    { id: 'kyc_c8', source: 'kyc_score', target: 'kyc_end',   sourceHandle: 'default' },
  ],
};

// Demo convenience — when the source canvas doesn't carry a `subWorkflowName`
// (as in the exported complex_claim_stress_test fixture) we fall back to
// matching on a slugified node label. Keeps the drill-in demo one click
// away without requiring fixture edits.
const LABEL_ALIAS = Object.freeze({
  health_subworkflow:     'health_claim_evaluation',
  start_payout_workflow:  'payout_processor',
  run_kyc_check:          'kyc_check',
});

function slugify(s) {
  return String(s || '').toLowerCase().trim().replace(/[\s-]+/g, '_');
}

/**
 * Registry keyed by refName. Returns the full canvas payload `{nodes,
 * connections}` for a given SubWorkflow's refName, or `null` if no child
 * is registered. Version is optional; we don't branch on it yet. When
 * refName is absent, falls back to a label-based lookup so demo fixtures
 * that only carry labels still resolve to a child.
 *
 * @param {string|null} refName
 * @param {number|null} [_refVersion]
 * @param {string|null} [labelHint]
 * @returns {{nodes: object[], connections: object[]} | null}
 */
export function lookupChildCanvas(refName, _refVersion, labelHint) {
  // 1. Exact refName hit.
  if (refName && CHILD_WORKFLOW_REGISTRY[refName]) {
    return CHILD_WORKFLOW_REGISTRY[refName];
  }
  // 2. Label alias hit.
  const slug = slugify(labelHint || refName || '');
  const aliased = LABEL_ALIAS[slug];
  if (aliased && CHILD_WORKFLOW_REGISTRY[aliased]) {
    return CHILD_WORKFLOW_REGISTRY[aliased];
  }
  return null;
}

export const CHILD_WORKFLOW_REGISTRY = Object.freeze({
  health_claim_evaluation: HEALTH_CLAIM_EVALUATION,
  payout_processor:        PAYOUT_PROCESSOR,
  kyc_check:               KYC_CHECK,
});
